import React from "react";
import { Body1, Header1, SolidButton, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Link, useTheme } from "@mui/material";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createLog } from "@iventis/observability-and-monitoring";
import { SUPPORT_EMAIL } from "@iventis/utilities";
import {
    testPixelStreamingConnection,
    testIfUrlIsReachable,
    testIfWSServerIsReachable,
    ReachableResult,
    PixelStreamingConnectionTestResult,
    WEBSOCKET_TIMEOUT,
    URL_TIMEOUT,
} from "./network-test";
import { Container, ErrorIcon, TickIcon } from "./digital-twin-components";
import { ConnectionStatus, NetworkStatusCard } from "./digital-twin-network-status-card";
import { DIGITAL_TWIN_TIMEOUT } from "./digital-twin-config";

interface ConnectionStatusState {
    status: ConnectionStatus;
    error?: string;
}
export interface DigitalTwinNetworkComponentProps {
    url: string;
    onReturnToMap: () => void;
}

export const DigitalTwinNetworkComponent: React.FunctionComponent<DigitalTwinNetworkComponentProps> = ({ url, onReturnToMap }) => {
    const theme = useTheme<Theme>();
    const translate = useIventisTranslate();
    const queryClient = useQueryClient();

    const { data: iventisConnection } = useQuery<ReachableResult, unknown, ConnectionStatusState>(["test-iventis"], () => testIfUrlIsReachable(window.location.origin), {
        select: (data) => {
            if (data == null) {
                return { status: ConnectionStatus.PENDING };
            }
            if (!data.resolved) {
                // Skip the other tests as they will fail if this does
                queryClient.setQueryData(["test-websocket"], { status: false });
                queryClient.setQueryData(["test-webrtc"], { status: false });
                createLog("User encountered digital twin network error, could not reach Iventis", "error", { error: data.error });
                return { status: ConnectionStatus.FAILURE, error: data.error };
            }
            return { status: ConnectionStatus.SUCCESS };
        },
        cacheTime: 0,
        refetchOnReconnect: false,
        initialData: null,
    });

    const { data: websocketConnection } = useQuery<ReachableResult, unknown, ConnectionStatusState>(
        ["test-websocket"],
        ({ signal }) => testIfWSServerIsReachable(`wss://${url}`, signal),
        {
            enabled: iventisConnection?.status === ConnectionStatus.SUCCESS,
            select: (data) => {
                if (data == null) {
                    return { status: ConnectionStatus.PENDING };
                }
                if (iventisConnection?.status === ConnectionStatus.FAILURE) {
                    return { status: ConnectionStatus.SKIPPED };
                }
                if (!data.resolved) {
                    // Skip the WebRTC as it will fail if this does
                    queryClient.setQueryData(["test-webrtc"], { status: false });
                    createLog("User encountered digital twin network error, could not reach WebSocket server", "error", { error: data.error });
                    return { status: ConnectionStatus.FAILURE, error: data.error };
                }
                return { status: ConnectionStatus.SUCCESS };
            },
            cacheTime: 0,
            refetchOnReconnect: false,
            initialData: null,
        }
    );

    const { data: webrtcConnection } = useQuery<PixelStreamingConnectionTestResult, unknown, ConnectionStatusState>(
        ["test-webrtc"],
        ({ signal }) => testPixelStreamingConnection(`wss://${url}`, signal),
        {
            enabled: websocketConnection?.status === ConnectionStatus.SUCCESS,
            select: (data) => {
                if (data == null) {
                    return { status: ConnectionStatus.PENDING };
                }
                if (websocketConnection?.status === ConnectionStatus.FAILURE || iventisConnection?.status === ConnectionStatus.FAILURE) {
                    return { status: ConnectionStatus.SKIPPED };
                }
                if (!data.resolved) {
                    createLog("User encountered digital twin network error, could not reach WebRTC server", "error", {
                        error: data.error,
                        connectionData: JSON.stringify(data.connectionData),
                    });
                    return { status: ConnectionStatus.FAILURE, error: data.error };
                }
                return { status: ConnectionStatus.SUCCESS };
            },
            cacheTime: 0,
            refetchOnReconnect: false,
            initialData: null,
        }
    );

    return (
        <Container style={{ backgroundColor: theme.secondaryColors.light30 }}>
            {webrtcConnection?.status === ConnectionStatus.SUCCESS ? (
                <>
                    <TickIcon />
                    <Header1>{translate(Content.map12.digital_twin_network.title_success)}</Header1>
                    <Body1>{translate(Content.map12.digital_twin_network.subtitle_success)}</Body1>
                </>
            ) : (
                <>
                    <ErrorIcon />
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                        <Header1>{translate(Content.map12.digital_twin_network.title)}</Header1>
                    </div>
                    <Body1>{translate(Content.map12.digital_twin_network.subtitle)}</Body1>
                </>
            )}
            <CardContainer>
                <NetworkStatusCard
                    title={translate(Content.map12.digital_twin_network.iventis_card.title)}
                    subtitle={translate(Content.map12.digital_twin_network.iventis_card.subtitle)}
                    status={iventisConnection.status}
                    error={iventisConnection.error}
                    action={translate(Content.map12.digital_twin_network.recommended_actions.iventis)}
                    timeoutTime={URL_TIMEOUT}
                />
                <NetworkStatusCard
                    title={translate(Content.map12.digital_twin_network.websocket_card.title)}
                    subtitle={translate(Content.map12.digital_twin_network.websocket_card.subtitle)}
                    status={websocketConnection.status}
                    error={websocketConnection.error}
                    action={translate(Content.map12.digital_twin_network.recommended_actions.websocket)}
                    timeoutTime={WEBSOCKET_TIMEOUT}
                />
                <NetworkStatusCard
                    title={translate(Content.map12.digital_twin_network.webRTC_card.title)}
                    subtitle={translate(Content.map12.digital_twin_network.webRTC_card.subtitle)}
                    status={webrtcConnection.status}
                    error={webrtcConnection.error}
                    action={translate(Content.map12.digital_twin_network.recommended_actions.webRTC)}
                    timeoutTime={DIGITAL_TWIN_TIMEOUT}
                />
            </CardContainer>
            <Body1>
                {translate(Content.map12.digital_twin_network.support_1)}
                {/** TODO: Replace this link */}
                <Link href="https://support.iventis.com/troubleshooting-the-digital-twin-connection">{translate(Content.map12.digital_twin_network.documentation)}</Link>
                {translate(Content.map12.digital_twin_network.support_2)} {SUPPORT_EMAIL}
            </Body1>
            <SolidButton onClick={onReturnToMap}>{translate(Content.map12.digital_twin_network.return_to_map)}</SolidButton>
        </Container>
    );
};

const CardContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: space-between;
`;
